import { useEffect, useState, useMemo, useCallback } from "react";
import {
  GridFilterModel,
  GridSortModel,
  GridColumnVisibilityModel,
} from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useAuth from "./useAuth";
import { fetchUserSettings, updateUserSettings } from "../api/userSettings";

export default function useGridUserSettings(tableName: string) {
  const { token } = useAuth();
  const qc = useQueryClient();

  const { data, isFetched } = useQuery({
    queryKey: ["userSettings", tableName],
    queryFn: () => fetchUserSettings(token, tableName),
    staleTime: Infinity,
  });

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!data) return;
    setFilterModel(
      data.filterModel ? JSON.parse(data.filterModel) : { items: [] }
    );
    setSortModel(data.sortModel ? JSON.parse(data.sortModel) : []);
    setColumnVisibilityModel(
      data.columnVisibilityModel ? JSON.parse(data.columnVisibilityModel) : {}
    );
    setColumnWidths(data.columnWidths ? JSON.parse(data.columnWidths) : {});
  }, [data]);

  const mutation = useMutation({
    mutationFn: (dto: any) => updateUserSettings(token, tableName, dto),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["userSettings", tableName] }),
  });

  useEffect(() => {
    if (!data) return;
    mutation.mutate({
      filterModel,
      sortModel,
      columnVisibilityModel,
      columnWidths,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterModel, sortModel, columnVisibilityModel, columnWidths]);

  const handleColumnResize = useCallback(
    (field: string, width: number) =>
      setColumnWidths((prev) => ({ ...prev, [field]: width })),
    []
  );

  const resetColumnWidths = useCallback(
    (fields: string[]) =>
      setColumnWidths(fields.reduce((acc, f) => ({ ...acc, [f]: 0 }), {})),
    []
  );

  return useMemo(
    () => ({
      filterModel,
      sortModel,
      columnVisibilityModel,
      columnWidths,
      isFetched,
      setFilterModel,
      setSortModel,
      setColumnVisibilityModel,
      setColumnWidths,
      handleColumnResize,
      resetColumnWidths,
    }),
    [
      filterModel,
      sortModel,
      columnVisibilityModel,
      columnWidths,
      isFetched,
      handleColumnResize,
      resetColumnWidths,
    ]
  );
}
